import { useDashboardFilters } from '../../utils/user/dashboardTimeRange';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getSelectedProject } from '../../redux/projects/actions';
import { setAlert } from '../../redux/dashboard/actions';
import { getUser } from '../../redux/user/actions';
import calendarUtils from '../../utils/calendar';
import API from '../../services/api';
import utils from '../../utils';
import { insightsTableHeaders, insightsTableData } from '../../utils/solutions';
import components from '../../components';
import icons from '../../assets/icons';
import './styles.scss';
import { getIntegrations } from '../../redux/integration/actions';
import { getSelectedAnalyticsDashboard } from '../../redux/templates/actions';
import { getCmsInfo } from '../../utils/integrations';

const { Table, DateTimeFilterBar } = components;

const InsightsContainer = ({ lastUpdated }) => {
  const project = useSelector(getSelectedProject);
  const user = useSelector(getUser);
  const integrations = useSelector(getIntegrations);
  const selectedTemplate = useSelector(getSelectedAnalyticsDashboard);

  const [campaignNavigation, setCampaignNavigation] = useState(null);
  const [data, setData] = useState([]); // Data returned by API
  const [items, setItems] = useState([]);
  const [itemsCount, setItemsCount] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [appliedFilters, setAppliedFilters] = useDashboardFilters(() => ({
    startDate: calendarUtils.getDefaultDate('start'),
    endDate: calendarUtils.getDefaultDate('end')
  }));
  const { startDate: startTime, endDate: endTime } = appliedFilters;
  const [filterByTime, setFilterByTime] = useState({});
  const [filterByWeekday, setFilterByWeekday] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const fetchIdRef = useRef(0);
  const dispatch = useDispatch();

  // Update after filters and redux changes
  useEffect(() => {
    if (user && selectedTemplate && !loading) {
      const integration = getCmsInfo(selectedTemplate.cmsId, integrations);
      const projectId = project ? project.id : '';
      const header = selectedTemplate.headers;
      const table = selectedTemplate.table;
      const datasource = selectedTemplate.compatibleDatasources[0]; //TODO: deal with multiple datasources in the future
      const body = generateRequestBody(integration);

      setLoading(true);

      API.getTableData(projectId, table, body)
        .then(res => {
          const { rows } = res;
          const tableHeaderParams = {
            cmsSchema: integration?.popColumnMappings,
            preferences: project.preferences,
            selectedCampaign: selectedCampaign
          };
          const columnsData = insightsTableHeaders[header](tableHeaderParams);
          const data = insightsTableData[datasource](rows);
          setColumns(columnsData);

          if (data && data.length > 0) {
            setData(data);
          } else {
            setData([]);
          }
        })
        .catch(err => {
          setData([]);
          dispatch(setAlert(utils.generateAlert('Something went wrong. Please try again.', 'error')));
          console.error(err);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [
    user,
    project,
    selectedTemplate,
    startTime,
    endTime,
    filterByTime,
    filterByWeekday,
    selectedCampaign,
    lastUpdated
  ]);

  useEffect(() => {
    // Reset selected campaign after changing project or solution
    setSelectedCampaign('');
    if (project && project.preferences) {
      setAppliedFilters({
        startDate: calendarUtils.getDefaultDate('start'),
        endDate: calendarUtils.getDefaultDate('end')
      });
    }
  }, [project, selectedTemplate]);

  useEffect(() => {
    if (selectedCampaign && selectedCampaign !== '') {
      setCampaignNavigation({ title: `Campaign: ${selectedCampaign}`, goBackButton: goBack });
    } else setCampaignNavigation(null);
  }, [selectedCampaign]);

  const generateRequestBody = integration => {
    const body = {
      layout: 'deviceGroup',
      startTime: appliedFilters.isTimeRangeLocked ? startTime : calendarUtils.replaceOffset(startTime, project.preferences.timezone),
      endTime: appliedFilters.isTimeRangeLocked ? endTime : calendarUtils.replaceOffset(endTime, project.preferences.timezone),
      filterByTime,
      filterByWeekday,
      groupBy: ['deviceId'],
      timezone: project.preferences.timezone
    };

    if (integration) {
      const columns = integration.popColumnMappings;

      if (selectedCampaign) {
        const filterBy = {
          column: columns.advertisers,
          value: selectedCampaign
        };

        return {
          ...body,
          filterBy,
          groupBy: [columns.medias, columns.sites]
        };
      }

      return {
        ...body,
        groupBy: [columns.advertisers, columns.sites]
      };
    }

    return body;
  };

  const handleRowClick = row => {
    if (selectedTemplate.isCms && !selectedCampaign) {
      const integration = getCmsInfo(selectedTemplate.cmsId, integrations);
      const { advertisers } = integration.popColumnMappings;
      setSelectedCampaign(row[advertisers]);
    }
  };

  const goBack = () => {
    setSelectedCampaign('');
  };

  const onPeriodSelected = payload => {
    const { startDate, endDate, filterByTime, filterByWeekday } = payload;
    setAppliedFilters(current => ({
      ...current,
      ...(startDate && { startDate }),
      ...(endDate && { endDate })
    }));
    setFilterByTime(filterByTime ? filterByTime : {});
    setFilterByWeekday(filterByWeekday ? filterByWeekday : []);
  };

  // This is called when the table needs new data
  const fetchData = useCallback(
    ({ pageIndex, pageSize, sortBy }) => {
      // Load table empty state
      if (data.length === 0) {
        setItems([]);
        setItemsCount(0);
        setPageCount(0);
        return;
      }

      // Give this fetch an ID
      const fetchId = ++fetchIdRef.current;

      // Generate sort config object
      const sortByQuery = sortBy[0];
      const sortByConfig = utils.generateSortByConfig(sortByQuery, 'deviceName', columns);

      // Perform data sort
      const items = utils.sort(data, sortByConfig);

      // Only update the data if this is the latest fetch
      if (fetchId === fetchIdRef.current) {
        const startRow = pageSize * pageIndex;
        const endRow = startRow + pageSize;

        // Retrieve fetched data
        setItems(items.slice(startRow, endRow));

        // Compute total number of items
        setItemsCount(items.length);

        // Compute total page count
        setPageCount(Math.ceil(items.length / pageSize));
      }
    },
    [data, columns]
  );

  if (!selectedTemplate) {
    return (
      <div className="no-data-message">
        <h1>No data found</h1>
      </div>
    );
  }

  return (
    <div className="comparison-container">
      <div className="filter-bar d-flex">
        <div className="title">
          <img src={icons.insights} alt="Compare" />
          <h2>{selectedTemplate.isCms ? `CMS Insights` : `Insights`}</h2>
        </div>
        <DateTimeFilterBar appliedFilters={appliedFilters} hideIntervalSelector onPeriodSelected={onPeriodSelected} />
      </div>
      {loading || data.length > 0 ? (
        <Table
          columns={columns}
          data={items}
          fetchData={fetchData}
          loading={loading || !project}
          navigation={campaignNavigation}
          hasInteractiveRows={!selectedCampaign && selectedTemplate.isCms}
          pageCount={pageCount}
          itemsCount={itemsCount}
          onItemSelected={handleRowClick}
          solution={selectedTemplate.headers}
        />
      ) : (
        <div className="no-data-message">
          <h1>No data</h1>
        </div>
      )}
    </div>
  );
};

export default InsightsContainer;
